import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";

import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import * as moment from 'moment-hijri';
import "moment/locale/ar";
import { UserInputMode, UserInputType } from "src/app/shared/models/interfaces";
import { ArabicDatePipe } from 'src/app/shared/pipes/arabic-date-pipe.pipe';
import { HijriDatePipe } from 'src/app/shared/pipes/hijri-date';
import { ConfirmRemoveBlacklistedDialogComponent } from "../dialogs/confirm-remove-blacklisted-dialog/confirm-remove-blacklisted-dialog.component";

export const HIJRI_MOMENT_DATE_FORMATS = {
  parse: {
    dateInput: 'iYYYY/iM/iD',
  },
  display: {
    dateInput: 'iYYYY/iM/iD',
    monthYearLabel: 'iYYYY/iM',
    dateA11yLabel: 'iYYYY/iM/iD',
    monthYearA11yLabel: 'iYYYY/iM',
  },
};

export const GREGORIAN_MOMENT_DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: "app-user-data-input",
  templateUrl: "./user-data-input.component.html",
  styleUrls: ["./user-data-input.component.scss"]
})
export class UserDataInputComponent implements OnInit {
  readonly UserInputMode = UserInputMode;
  readonly UserInputType = UserInputType;

  readonly MAXIMUM_IMAGE_SIZE = 2097152;
  isImageInvalid = false;
  actualImg: string | ArrayBuffer | null = null;

  control: FormControl;
  @Input() formGroupParent: FormGroup;
  @Input() mode: UserInputMode;
  @Input() userData: string | boolean | number;
  @Input() inputType: UserInputType;
  @Input() controlName: string;
  @Input() maxValue: string;
  @Input() readonly = false;
  @Input() listData: string[];
  checkedValues = [];
  calendarType: 'hijri' | 'gregorian' = 'gregorian';
  @Input() set isHijriDate(val: boolean) {
    this.calendarType = val ? 'hijri' : 'gregorian';
  }
  get isHijriDate(): boolean {
    return this.calendarType === 'hijri';
  }
  @Output() calendarTypeChange = new EventEmitter<string>();

  constructor(
    private _dialog: MatDialog,
    private arabicDatePipe: ArabicDatePipe,
    private hijriDatePipe: HijriDatePipe
  ) {
  }

  ngOnInit(): void {
    if (this.controlName === "blackListed") {
      this.userData = !!this.userData;
    }
    this.control = <FormControl>this.formGroupParent.get(this.controlName);

    // Set initial calendar type based on userData/additionalData
    if (this.inputType === this.UserInputType.DATE) {
      let additionalData = null;
      if (typeof this.userData === 'object' && this.userData !== null) {
        additionalData = this.userData;
      } else if (this.userData && this['additionalData']) {
        additionalData = this['additionalData'];
      }

      // Default to Gregorian if no dates exist
      this.calendarType = 'gregorian';

      if (additionalData) {
        if (additionalData.birthdateHijri && !additionalData.birthdate) {
          this.calendarType = 'hijri';
          // Set the form control value to the Hijri date
          if (this.control) {
            try {
              const hijriMoment = moment(additionalData.birthdateHijri, ['iYYYY/iM/iD', 'iYYYY-iM-iD', 'iD/iM/iYYYY', 'iYYYY-iMM-iDD']);
              if (hijriMoment.isValid()) {
                this.control.setValue(hijriMoment.toDate());
              } else {
                this.control.setValue(additionalData.birthdateHijri);
              }
            } catch (error) {
              console.error('Error parsing initial Hijri date:', error);
              this.control.setValue(additionalData.birthdateHijri);
            }
          }
        } else if (additionalData.birthdate) {
          this.calendarType = 'gregorian';
          // Set the form control value to the Gregorian date
          if (this.control) {
            try {
              const gregorianMoment = moment(additionalData.birthdate);
              if (gregorianMoment.isValid()) {
                this.control.setValue(gregorianMoment.toDate());
              } else {
                this.control.setValue(additionalData.birthdate);
              }
            } catch (error) {
              console.error('Error parsing initial Gregorian date:', error);
              this.control.setValue(additionalData.birthdate);
            }
          }
        }
      }
    }
    this.calendarTypeChange.emit(this.calendarType);

    if (
      this.inputType === this.UserInputType.CHECK &&
      this.control.value.length &&
      Array.isArray(this.control.value)
    )
      this.checkedValues = this.control.value;
  }

  onImgUploaded(event) {
    const uploadedImg = event.target.files[0];
    if (!uploadedImg) return;

    // Not allow to upload image which size is equal or more than 2MB
    this.isImageInvalid = uploadedImg.size >= this.MAXIMUM_IMAGE_SIZE;

    this.control.setValue(uploadedImg);
  }

  removeImg() {
    this.control.setValue(null);
    this.actualImg = null;
  }

  get isUserDataBoolean() {
    return this.userData == 1 || this.userData == 0;
  }

  isControlRequired(field: string) {
    const form_field = this.formGroupParent.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return validator && validator.required;
  }

  //check checkbox array to push or pop any checked or unchecked value
  checkValueHandler(event: any) {
    const isChecked = event.target.getAttribute("aria-checked") == "true";
    const value = event.target.getAttribute("value");
    if (isChecked) this.checkedValues.push(value);
    if (!isChecked)
      this.checkedValues = this.checkedValues.filter((item) => {
        return item != value;
      });

    this.control.setValue(this.checkedValues);
  }

  addToMultipleText(input: HTMLInputElement): void {
    const value = input.value;

    // Add our fruit
    if ((value || "").trim()) {
      this.control.value.push(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = "";
    }

    this.control.updateValueAndValidity();
  }

  removeFromMultipleText(value: any): void {
    const index = this.control.value.indexOf(value);

    if (index >= 0) {
      this.control.value.splice(index, 1);
    }

    this.control.updateValueAndValidity();
  }

  //valid pattern
  validatePhone() {
    const phoneValue = this.control.value.replace(/^05/, "5");
    this.control.setValue(phoneValue, {
      emitModelToViewChange: false,
    });
  }

  onBooleanRadioChange(event: any) {
    const newValue = event.value;
    if (this.controlName === "blackListed" && newValue === false) {
      const dialogRef = this._dialog.open(
        ConfirmRemoveBlacklistedDialogComponent,
        {
          width: "57rem",
          maxHeight: "80vh",
        }
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (!result) {
          // User cancelled - revert to true/1
          if (this.control) {
            this.control.setValue(this.isUserDataBoolean ? true : 1);
          }
        }
      });
    }
  }

  onCalendarTypeChange(event: any) {
    // Set the appropriate date value when switching calendar types
    if (this.control && this.isUserDataObj(this.userData)) {
      if (event.value === 'hijri' && this.userData.birthdateHijri) {
        // Convert Hijri date string to proper moment-hijri object for the date picker
        const hijriDateString = this.userData.birthdateHijri;

        try {
          // Try to parse the Hijri date string and convert it to a moment object
          let hijriMoment;
          if (typeof hijriDateString === 'string') {
            // Parse different possible Hijri date formats
            hijriMoment = moment(hijriDateString, ['iYYYY/iM/iD', 'iYYYY-iM-iD', 'iD/iM/iYYYY', 'iYYYY-iMM-iDD']);
            if (!hijriMoment.isValid()) {
              // Try parsing as regular date and convert to Hijri
              hijriMoment = moment(hijriDateString);
            }
          } else {
            hijriMoment = moment(hijriDateString);
          }

          this.control.setValue(hijriMoment.toDate());
        } catch (error) {
          console.error('Error parsing Hijri date:', error);
          this.control.setValue(hijriDateString);
        }
      } else if (event.value === 'gregorian' && this.userData.birthdate) {
        // Convert Gregorian date string to proper format for the date picker
        const gregorianDateString = this.userData.birthdate;

        try {
          const gregorianMoment = moment(gregorianDateString);
          this.control.setValue(gregorianMoment.toDate());
        } catch (error) {
          console.error('Error parsing Gregorian date:', error);
          this.control.setValue(gregorianDateString);
        }
      } else {
        this.control.setValue(null);
      }
      this.calendarTypeChange.emit(this.calendarType);
    }
  }

  private isUserDataObj(data: any): data is { birthdate?: string; birthdateHijri?: string } {
    return typeof data === 'object' && data !== null && ('birthdate' in data || 'birthdateHijri' in data);
  }

  getDisplayDate(): string {
    if (!this.userData || this.inputType !== this.UserInputType.DATE) return '';
    let gregorian: any;
    let hijri: any;
    if (this.isUserDataObj(this.userData)) {
      gregorian = this.userData.birthdate;
      hijri = this.userData.birthdateHijri;
    } else {
      gregorian = this.userData;
      hijri = this.userData;
    }
    if (!this.isHijriDate) {
      return this.arabicDatePipe.transform(gregorian, 'Do MMM yy') || gregorian || '';
    } else {
      return this.hijriDatePipe.transform(hijri, 'hijri') || hijri || '';
    }
  }
}
